import React, { useState, useEffect } from 'react';
import { useAnnouncements, ANNOUNCEMENT_CATEGORIES } from '../contexts/AnnouncementContext';
import { testAnnouncementsAccess } from '../utils/FirebaseUtils';
import './Announcements.css';

function Announcements({ limit, showTitle = true }) {
  const { announcements, loading, error } = useAnnouncements();
  const [activeCategory, setActiveCategory] = useState('All');
  const [errorDetails, setErrorDetails] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  
  let filteredAnnouncements = activeCategory === 'All' 
    ? announcements 
    : announcements.filter(announcement => announcement.category === activeCategory);

  // Apply limit if provided
  if (limit && !isNaN(limit) && limit > 0) {
    filteredAnnouncements = filteredAnnouncements.slice(0, limit);
  }

  // Effect to handle errors when they occur
  useEffect(() => {
    if (error) {
      console.error('Announcements error detected:', error);
      setErrorDetails({ message: error, timestamp: new Date().toISOString() });
    } else {
      setErrorDetails(null);
    }
  }, [error]);

  // Add escape key listener for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedAnnouncement) {
        setSelectedAnnouncement(null);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [selectedAnnouncement]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Truncate long content to display a preview
  const truncateContent = (text, maxLength = 120) => {
    if (!text || text.length <= maxLength) return text;
    // Find the last space before maxLength to avoid cutting words
    const lastSpace = text.substring(0, maxLength).lastIndexOf(' ');
    return text.slice(0, lastSpace > 0 ? lastSpace : maxLength) + '...';
  };

  const handleRetry = async () => {
    setRetrying(true);
    
    try {
      // First, test if the announcements node is accessible
      const testResult = await testAnnouncementsAccess();
      
      if (testResult.success) {
        // If accessible, force a page reload to reconnect
        window.location.reload();
      } else {
        // If not accessible, show more detailed error
        setErrorDetails({
          ...errorDetails,
          testResult,
          message: `Cannot access announcements: ${testResult.message}`,
          timestamp: new Date().toISOString()
        });
      }
    } catch (err) {
      setErrorDetails({
        ...errorDetails,
        retryError: err.message,
        message: `Error during retry: ${err.message}`,
        timestamp: new Date().toISOString()
      });
    } finally {
      setRetrying(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="announcements-section">
        <div className="announcements-loading">
          <div className="loading-spinner"></div>
          <p>Loading announcements...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="announcements-section">
        <div className="announcements-error">
          <h3>Error loading announcements</h3>
          <p>{error}</p>
          <button 
            className="retry-button" 
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? 'Checking connection...' : 'Retry'}
          </button>
          {errorDetails && errorDetails.testResult && !errorDetails.testResult.success && (
            <p className="connection-info">
              Firebase connection issue detected. Please check your internet connection.
            </p>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="announcements-section">
      {showTitle && (
        <div className="category-filter">
          <button
            className={`category-btn ${activeCategory === 'All' ? 'active' : ''}`}
            onClick={() => setActiveCategory('All')}
          >
            All
          </button>
          {ANNOUNCEMENT_CATEGORIES.map(category => (
            <button
              key={category}
              className={`category-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      )}
      
      <div className="announcements-container">
        {filteredAnnouncements.length > 0 ? (
          filteredAnnouncements.map(announcement => (
            <div className="announcement-card" key={announcement.id}>
              {announcement.imageBase64 && (
                <div className="announcement-image-container">
                  <img 
                    src={announcement.imageBase64} 
                    alt={announcement.title} 
                    className="announcement-image"
                    onClick={() => setSelectedAnnouncement(announcement)}
                    onError={(e) => {
                      console.error('Error loading image:', e);
                      e.target.style.display = 'none';
                    }} 
                  />
                </div>
              )}
              
              <div className="announcement-header">
                <span className={`announcement-category badge-${announcement.category ? announcement.category.toLowerCase() : 'general'}`}>
                  {announcement.category || 'General'}
                </span>
                <span className="announcement-date">{formatDate(announcement.createdAt)}</span>
              </div>
              
              <h3 className="announcement-title" onClick={() => setSelectedAnnouncement(announcement)}>
                {announcement.title}
              </h3>
              
              <div className="announcement-content">
                {announcement.content && 
                  truncateContent(announcement.content)
                    .split('\n')
                    .map((paragraph, index) => (
                      paragraph ? <p key={index}>{paragraph}</p> : null
                    ))
                }
              </div>
              
              <button 
                className="read-more-btn"
                onClick={() => setSelectedAnnouncement(announcement)}
              >
                Read More
              </button>
            </div>
          ))
        ) : (
          <div className="no-announcements">
            {activeCategory === 'All' 
              ? 'No announcements available at this time.' 
              : `No announcements in the ${activeCategory} category at this time.`}
          </div>
        )}
      </div>
      
      {/* Announcement Detail Modal */}
      {selectedAnnouncement && (
        <div className="announcement-modal-overlay" onClick={() => setSelectedAnnouncement(null)}>
          <div className="announcement-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedAnnouncement(null)}>×</button>
            
            <div className="modal-header">
              <h2>{selectedAnnouncement.title}</h2>
              <div className="modal-meta">
                <span className={`badge-${selectedAnnouncement.category ? selectedAnnouncement.category.toLowerCase() : 'general'}`}>
                  {selectedAnnouncement.category || 'General'}
                </span>
                <span className="date">{formatDate(selectedAnnouncement.createdAt)}</span>
              </div>
            </div>
            
            {selectedAnnouncement.imageBase64 && (
              <div className="modal-image-container">
                <img 
                  src={selectedAnnouncement.imageBase64} 
                  alt={selectedAnnouncement.title} 
                  className="modal-image" 
                />
              </div>
            )}
            
            <div className="modal-content">
              {selectedAnnouncement.content && 
                selectedAnnouncement.content
                  .split('\n')
                  .map((paragraph, index) => (
                    paragraph ? <p key={index}>{paragraph}</p> : null
                  ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Announcements; 